<template>
  <div class="row">
    <GridPanelHeader :title="''" :show-add="true" :show-export="false" :show-columns="false" @add="addDocDate" />
    <DxDataGrid
      ref="grid"
      :data-source="rows"
      :show-borders="true"
      key-expr="uuid"
      :row-alternation-enabled="true"
      :column-auto-width="true"
      :export="exportOptions"
      :on-initialized="onGridInitialized"
      :on-row-inserting="onRowInserting"
      :on-row-updating="onRowUpdating"
      :on-row-inserted="onRowInserted"
      :on-row-updated="onRowUpdated"
      :on-row-removed="onRowRemoved"
      :on-editor-preparing="onEditorPreparing"
      :element-attr="gridAttributes"
    >
      <DxColumn
        data-field="warning_type"
        :caption="$t('common.document_type')"
        :lookup="{ dataSource: documentTypes, valueExpr: 'code', displayExpr: 'code' }"
      >
      </DxColumn>
      <DxColumn data-field="name" :caption="$t('common.name')"> </DxColumn>
      <DxColumn
        data-field="expiration_date"
        data-type="date"
        :format="this.getDxGridDefaultDateFormat"
        :caption="$t('common.expiration_date')"
      ></DxColumn>
      <DxColumn data-field="notes" :caption="$t('remark')"> </DxColumn>
      <DxEditing mode="row" :allow-adding="false" :allow-updating="true" :allow-deleting="true" :use-icons="true" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, { DxColumn, DxEditing } from "devextreme-vue/data-grid";
import notify from "devextreme/ui/notify";
import { generateUUID } from "@/helpers/common";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "WorkMachineExpirationStep",
  components: {
    GridPanelHeader,
    DxDataGrid,
    DxColumn,
    DxEditing,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const initial = Array.isArray(this.value) ? this.value : [];
    return {
      rows: initial.map((r) => ({ ...r })),
      documentTypes: [],
      grid: null,
      exportOptions: {
        enabled: false,
      },
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    gridAttributes() {
      return {
        class: "smagria-grid work-machine-expiration-grid",
      };
    },
  },
  methods: {
    ...mapActions({
      getWorkMachineExpirationDocumentTypes: "works-work-machines/getWorkMachineExpirationDocumentTypes",
    }),
    loadDocumentTypes() {
      this.getWorkMachineExpirationDocumentTypes({
        enablePagination: 0,
        limit: 9999,
      }).then((resp) => {
        // The action may return either response.data or the raw array
        const payload = resp && resp.data ? resp.data : resp;
        const items = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        this.documentTypes = items;
        //this.documentTypesGrid = items.filter((item) => !item.fixed && item.fixed !== 1);

        // Ensure there is at least one row for each document type (by warning_type)
        if (!Array.isArray(this.rows)) {
          this.rows = [];
        }
        let added = false;
        this.documentTypes.forEach((type) => {
          const code = type.code != null ? type.code : type.value;
          const label = type.name != null ? type.name : type.label;
          if (!code) {
            return;
          }
          const existingRow = this.rows.find((r) => r && r.warning_type === code);
          if (!existingRow && type.fixed) {
            this.rows.push({
              uuid: generateUUID(),
              warning_type: code,
              name: label != null ? label : code,
              expiration_date: null,
              type_uuid: type.uuid || type.id || null,
              is_fixed: type.fixed || false,
              notes: "",
            });
            added = true;
          } else if (existingRow) {
            // Update existing row with type info
            existingRow.is_fixed = !!(type.fixed || false);
          }
        });
        if (added) {
          this.emitChange();
        }
      });
    },
    onGridInitialized(e) {
      this.grid = e && e.component ? e.component : null;
    },
    resolveGrid() {
      if (this.grid) return this.grid;
      const comp = this.$refs.grid;
      if (comp && comp.instance) {
        this.grid = comp.instance;
        return this.grid;
      }
      return null;
    },
    addDocDate() {
      const grid = this.resolveGrid();
      if (grid && typeof grid.addRow === "function") {
        grid.addRow();
      }
    },
    applyTypeInfo(row) {
      if (!row || !row.warning_type) {
        row.is_fixed = false;
        return;
      }
      const type = this.documentTypes.find((t) => t.code === row.warning_type);
      if (!type) {
        row.is_fixed = false;
        return;
      }
      row.type_uuid = type.uuid || type.id || null;
      row.is_fixed = !!(type.fixed || type.is_unique);
      if (row.is_fixed) {
        row.name = type.name;
      }
    },
    emitChange() {
      this.$emit(
        "input",
        this.rows.map((r) => ({ ...r })),
      );
    },
    isFixedDuplicate(row, excludeId = null) {
      if (!row || !row.is_fixed || !row.warning_type) {
        return false;
      }
      return this.rows.some((r) => r.uuid !== excludeId && r.is_fixed && r.warning_type === row.warning_type);
    },
    onRowInserting(e) {
      if (!e.data.uuid) {
        e.data.uuid = generateUUID();
      }
      this.applyTypeInfo(e.data);
      if (this.isFixedDuplicate(e.data)) {
        e.cancel = true;
        notify({
          message: this.$t("works.work_machines.duplicate_document_type"),
          type: "warning",
          displayTime: 2500,
          width: "auto",
          position: { at: "top center", my: "top center" },
        });
      }
    },
    onRowUpdating(e) {
      const merged = Object.assign({}, e.oldData, e.newData);
      this.applyTypeInfo(merged);
      if (this.isFixedDuplicate(merged, e.key)) {
        e.cancel = true;
        notify({
          message: this.$t("works.work_machines.duplicate_document_type"),
          type: "warning",
          displayTime: 2500,
          width: "auto",
          position: { at: "top center", my: "top center" },
        });
        return;
      }
      // propagate computed fields back to the row
      e.newData.warning_type = merged.warning_type;
      e.newData.name = merged.name;
      e.newData.type_uuid = merged.type_uuid;
      e.newData.is_fixed = merged.is_fixed;
      e.newData.notes = merged.notes;
    },
    onRowInserted() {
      this.emitChange();
    },
    onRowUpdated() {
      this.emitChange();
    },
    onRowRemoved() {
      this.emitChange();
    },
    onEditorPreparing(e) {
      if (e.parentType === "dataRow" && e.dataField === "name") {
        const row = e.row && e.row.data;
        if (row && row.is_fixed) {
          e.editorOptions.readOnly = true;
        }
      }
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        const initial = Array.isArray(newVal) ? newVal : [];
        this.rows = initial.map((r) => ({ ...r }));
      },
    },
  },
  mounted() {
    this.loadDocumentTypes();
  },
};
</script>
<style lang="scss">
.work-machine-expiration-grid {
  height: 100%;
}
</style>
